import { ComparisonLimits, ComparisonSelectionTotals } from '@/types/chat/chat';

export const COMPARISON_LIMIT_REASONS = {
  fileCount: 'file_count',
  totalSizeMb: 'total_size_mb',
} as const;

export type ComparisonLimitReason =
  (typeof COMPARISON_LIMIT_REASONS)[keyof typeof COMPARISON_LIMIT_REASONS];

export type ComparisonSelectionCheck =
  | { ok: true }
  | {
      ok: false;
      reason: ComparisonLimitReason;
      current: number;
      max: number;
    };

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Client-side Cross-check Mode gate. Missing or partial data is treated as
 * ok so the server remains the authority.
 */
export const checkComparisonSelection = (
  totals?: ComparisonSelectionTotals | null,
  limits?: ComparisonLimits | null,
): ComparisonSelectionCheck => {
  if (
    totals == null ||
    limits == null ||
    !isFiniteNumber(totals.file_count) ||
    !isFiniteNumber(totals.total_size_mb) ||
    !isFiniteNumber(limits.max_files) ||
    !isFiniteNumber(limits.max_total_size_mb)
  ) {
    return { ok: true };
  }

  const roundedSize = Math.round(totals.total_size_mb * 100) / 100;

  if (totals.file_count > limits.max_files) {
    return {
      ok: false,
      reason: COMPARISON_LIMIT_REASONS.fileCount,
      current: totals.file_count,
      max: limits.max_files,
    };
  }

  if (roundedSize > limits.max_total_size_mb) {
    return {
      ok: false,
      reason: COMPARISON_LIMIT_REASONS.totalSizeMb,
      current: roundedSize,
      max: limits.max_total_size_mb,
    };
  }

  return { ok: true };
};

export const comparisonLimitErrorKey = (
  reason: ComparisonLimitReason,
):
  | 'home.chat.errors.comparisonFileCount'
  | 'home.chat.errors.comparisonTotalSize' =>
  reason === COMPARISON_LIMIT_REASONS.fileCount
    ? 'home.chat.errors.comparisonFileCount'
    : 'home.chat.errors.comparisonTotalSize';
